import type { SyntheticEvent } from 'react'
import type { StringColorProps } from 'types/password'
// types
import type { SnackbarProps } from 'types/snackbar'

// assets
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'

// material-ui
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material'
import { openSnackbar } from 'api/snackbar'

import AnimateButton from 'components/@extended/AnimateButton'
import IconButton from 'components/@extended/IconButton'
import { Formik } from 'formik'
// project import
import useAuth from 'hooks/useAuth'
import { postWithTokenWithStatus } from 'hooks/http'

import useScriptRef from 'hooks/useScriptRef'
import { useEffect, useState } from 'react'

import { useNavigate } from 'react-router-dom'
import { strengthColor, strengthIndicator } from 'utils/password-strength'

// third party
import * as Yup from 'yup'

// ============================|| REQUIRE PASSWORD CHANGE FORM ||============================ //

function RequirePasswordChangeForm() {
  const scriptedRef = useScriptRef()
  const navigate = useNavigate()

  const { token } = useAuth()

  const [level, setLevel] = useState<StringColorProps>()
  const [showPassword, setShowPassword] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword)
  }

  const handleClickShowCurrentPassword = () => {
    setShowCurrentPassword(!showCurrentPassword)
  }

  const handleMouseDownPassword = (event: SyntheticEvent) => {
    event.preventDefault()
  }

  const changePassword = (value: string) => {
    const temp = strengthIndicator(value)
    setLevel(strengthColor(temp))
  }

  useEffect(() => {
    changePassword('')
  }, [])

  return (
    <Formik
      initialValues={{
        currentPassword: '',
        password: '',
        confirmPassword: '',
        submit: null,
      }}
      validationSchema={Yup.object().shape({
        currentPassword: Yup.string().required('Current password is required'),
        password: Yup.string()
          .min(8, 'Password must be at least 8 characters')
          .max(255)
          .required('Password is required')
          .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
            'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
          ),
        confirmPassword: Yup.string()
          .required('Confirm Password is required')
          .test(
            'confirmPassword',
            'Both passwords must match!',
            (confirmPassword, yup) => yup.parent.password === confirmPassword
          ),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          if (!token) {
            throw new Error('No authentication token available')
          }

          // Call API to change password
          const response = await postWithTokenWithStatus('auth/change-password', token, {
            currentPassword: values.currentPassword,
            newPassword: values.password,
          })

          if (response.status === 200 || response.status === 201) {
            if (scriptedRef.current) {
              setStatus({ success: true })
              setSubmitting(false)

              openSnackbar({
                open: true,
                message: 'Password successfully changed. Please log in with your new password.',
                variant: 'alert',
                alert: {
                  color: 'success',
                },
              } as SnackbarProps)

              // Navigate to success page which will handle logout
              setTimeout(() => {
                navigate('/password-reset-success', { replace: true })
              }, 1000)
            }
          } else {
            throw new Error(response.data?.message || 'Failed to change password')
          }
        } catch (err: any) {
          console.error(err)
          if (scriptedRef.current) {
            setStatus({ success: false })
            setErrors({ submit: err.message || 'An error occurred while changing your password' })
            setSubmitting(false)

            openSnackbar({
              open: true,
              message: err.message || 'Failed to change password',
              variant: 'alert',
              alert: {
                color: 'error',
              },
            } as SnackbarProps)
          }
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <InputLabel htmlFor="current-password">Current Password</InputLabel>
                <OutlinedInput
                  fullWidth
                  error={Boolean(touched.currentPassword && errors.currentPassword)}
                  id="current-password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={values.currentPassword}
                  name="currentPassword"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle current password visibility"
                        onClick={handleClickShowCurrentPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        color="secondary"
                      >
                        {showCurrentPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                      </IconButton>
                    </InputAdornment>
                  }
                  placeholder="Enter current password"
                />
              </Stack>
              {touched.currentPassword && errors.currentPassword && (
                <FormHelperText error id="helper-text-current-password">
                  {errors.currentPassword}
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={12}>
              <Stack spacing={1}>
                <InputLabel htmlFor="password-change">New Password</InputLabel>
                <OutlinedInput
                  fullWidth
                  error={Boolean(touched.password && errors.password)}
                  id="password-change"
                  type={showPassword ? 'text' : 'password'}
                  value={values.password}
                  name="password"
                  onBlur={handleBlur}
                  onChange={(e) => {
                    handleChange(e)
                    changePassword(e.target.value)
                  }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        color="secondary"
                      >
                        {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                      </IconButton>
                    </InputAdornment>
                  }
                  placeholder="Enter new password"
                />
              </Stack>
              {touched.password && errors.password && (
                <FormHelperText error id="helper-text-password-change">
                  {errors.password}
                </FormHelperText>
              )}
              <FormControl fullWidth sx={{ mt: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <Box sx={{ bgcolor: level?.color, width: 85, height: 8, borderRadius: '7px' }} />
                  </Grid>
                  <Grid item>
                    <Typography variant="subtitle1" fontSize="0.75rem">
                      {level?.label}
                    </Typography>
                  </Grid>
                </Grid>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Stack spacing={1}>
                <InputLabel htmlFor="confirm-password-change">Confirm New Password</InputLabel>
                <OutlinedInput
                  fullWidth
                  error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                  id="confirm-password-change"
                  type="password"
                  value={values.confirmPassword}
                  name="confirmPassword"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="Confirm new password"
                />
              </Stack>
              {touched.confirmPassword && errors.confirmPassword && (
                <FormHelperText error id="helper-text-confirm-password-change">
                  {errors.confirmPassword}
                </FormHelperText>
              )}
            </Grid>

            {errors.submit && (
              <Grid item xs={12}>
                <FormHelperText error>{errors.submit}</FormHelperText>
              </Grid>
            )}
            <Grid item xs={12}>
              <AnimateButton>
                <Button
                  disableElevation
                  disabled={isSubmitting}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Change Password
                </Button>
              </AnimateButton>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  )
}

export default RequirePasswordChangeForm
